import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import styles from './DashboardPage.module.css';

interface Edition {
  id: string;
  title: string;
  date: string;
  cover_url?: string;
}

const DashboardPage: React.FC = () => {
  const { member } = useAuth();
  const [referralInput, setReferralInput] = useState('');
  const [referralStatus, setReferralStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [referralError, setReferralError] = useState('');
  const [editions, setEditions] = useState<Edition[]>([]);
  const [editionsLoading, setEditionsLoading] = useState(true);

  useEffect(() => {
    const fetchEditions = async () => {
      try {
        const { data, error } = await supabase
          .from('b2b_editions')
          .select('id, title, date, cover_url')
          .lt('date', new Date().toISOString().split('T')[0])
          .order('date', { ascending: false })
          .limit(4);

        if (error) throw error;
        setEditions(data || []);
      } catch {
        setEditions([]);
      } finally {
        setEditionsLoading(false);
      }
    };

    fetchEditions();
  }, []);

  const handleReferral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!referralInput.trim() || !member) return;

    setReferralStatus('sending');
    setReferralError('');

    const isEmail = referralInput.includes('@');
    const payload = {
      sponsor_id: member.id,
      sponsor_name: member.name,
      ...(isEmail
        ? { referred_email: referralInput.trim() }
        : { referred_linkedin: referralInput.trim() }),
      status: 'pending',
    };

    try {
      const { error } = await supabase.from('b2b_referrals').insert(payload);
      if (error) throw error;
    } catch {
      // Fallback: save to localStorage
      const pending = JSON.parse(localStorage.getItem('b2b_pending_referrals') || '[]');
      pending.push(payload);
      localStorage.setItem('b2b_pending_referrals', JSON.stringify(pending));
    }

    // Best-effort notification
    try {
      await fetch('/.netlify/functions/notify-referral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sponsor_name: member.name,
          ...(isEmail
            ? { referred_email: referralInput.trim() }
            : { referred_linkedin: referralInput.trim() }),
        }),
      });
    } catch {
      // silent
    }

    setReferralInput('');
    setReferralStatus('success');
    setTimeout(() => setReferralStatus('idle'), 5000);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <span className={styles.badge}>
            <span className={styles.greenDot} />
            Member Area
          </span>
          <h1 className={styles.title}>Welcome, {member?.name}</h1>
          <p className={styles.subtitle}>Your private B2B space</p>
        </header>

        <div className={styles.grid}>
          <Link to="/members/editions" className={styles.card} data-accent="purple">
            <span className={styles.cardIcon}>📅</span>
            <h3 className={styles.cardTitle}>Editions</h3>
            <p className={styles.cardDesc}>Browse past and upcoming editions</p>
          </Link>

          <Link to="/members/directory" className={styles.card} data-accent="cyan">
            <span className={styles.cardIcon}>👥</span>
            <h3 className={styles.cardTitle}>Directory</h3>
            <p className={styles.cardDesc}>Find and connect with members</p>
          </Link>

          <div className={`${styles.card}`} data-accent="yellow">
            <span className={styles.cardIcon}>🤝</span>
            <h3 className={styles.cardTitle}>Sponsor a Member</h3>
            <p className={styles.cardDesc}>Invite someone to join</p>
            <form onSubmit={handleReferral} className={styles.referralForm}>
              <input
                type="text"
                placeholder="Email or LinkedIn URL"
                value={referralInput}
                onChange={(e) => setReferralInput(e.target.value)}
                className={styles.referralInput}
                disabled={referralStatus === 'sending'}
              />
              <button
                type="submit"
                className={styles.referralButton}
                disabled={referralStatus === 'sending' || !referralInput.trim()}
              >
                {referralStatus === 'sending' ? '...' : 'Send'}
              </button>
            </form>
            {referralStatus === 'success' && (
              <p className={styles.referralSuccess}>Referral submitted!</p>
            )}
            {referralStatus === 'error' && (
              <p className={styles.referralError}>{referralError || 'An error occurred.'}</p>
            )}
          </div>
        </div>

        <section className={styles.editionsSection}>
          <h2 className={styles.sectionTitle}>Latest Editions</h2>
          {editionsLoading ? (
            <p className={styles.loadingText}>Loading...</p>
          ) : editions.length === 0 ? (
            <p className={styles.loadingText}>No editions yet.</p>
          ) : (
            <div className={styles.editionsGrid}>
              {editions.map((edition) => (
                <Link
                  key={edition.id}
                  to={`/members/editions/${edition.id}`}
                  className={styles.editionCard}
                >
                  <div className={styles.editionCover}>
                    {edition.cover_url ? (
                      <img src={edition.cover_url} alt={edition.title} />
                    ) : (
                      <div className={styles.editionPlaceholder} />
                    )}
                    <span className={styles.editionDate}>{formatDate(edition.date)}</span>
                  </div>
                  <h4 className={styles.editionTitle}>{edition.title}</h4>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
